<template>
  <div
    class="content-grid-list-item w-full flex items-center py-2"
    :class="{
      'opacity-50': enrichedFile.is_deleted,
      'border-b-solid border-primary5': !isLast,
    }"
  >
    <div class="flex items-center">
      <div class="my-3 border border-solid border-primary6 rounded">
        <CFileThumbnail
          v-if="thumbnailItem && !launchDarkly.enable_ai_generated_thumbnails && hasValidThumbnail"
          :item="thumbnailItem as RenderableFile"
        />
        <CImage
          v-else-if="launchDarkly.enable_ai_generated_thumbnails && enrichedFile && hasValidThumbnail"
          :ai-img-description="enrichedFile.name"
          class="rounded-2 overflow-hidden"
          :file-data="enrichedFile"
          height="100"
          :img-props="{ draggable: false }"
          :lazy="isDefaultDisplayMode"
          object-fit="cover"
          preview-disabled
          :src="thumbnailUrl"
          width="152"
          @error="handleImageError"
        />
        <div v-else class="w-[152px] h-[100px] rounded-2 overflow-hidden">
          <img
            v-if="defaultThumbnailUrl"
            :alt="enrichedFile.name || 'File thumbnail'"
            class="w-full h-full object-cover"
            :src="defaultThumbnailUrl"
          />
          <div v-else class="bg-primary6 w-full h-full flex items-center justify-center">
            <CIcon :color="themeVars.primary3" :icon="defaultIcon" size="32px" />
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col justify-center gap-1 pa-3 truncate w-full">
      <div class="font-700 font-size-[18px] whitespace-normal break-words overflow-visible">
        {{ enrichedFile.name }}
        <span v-if="item.type === 'slide'" class="color-text3 ml-1">
          ({{ t('canvasUI.files.slideNumber', { number: item.slide.index + 1 }) }})
        </span>
      </div>

      <div class="truncate font-size-[12px]" :style="{ color: themeVars.text3 }">
        <component :is="renderContentType(enrichedFile.content_type || FileContentTypeEnum.PDF)" />
      </div>

      <div class="flex flex-wrap gap-2 items-center">
        <CDynamicIndicator
          v-for="metadataProperty of pitcherSettings.metadata_highlight_properties ?? []"
          :key="metadataProperty.key"
          :item="enrichedFile"
          :metadataProperty="metadataProperty"
        />
      </div>

      <!-- Tags -->
      <CTableTag
        v-if="enrichedFile.tags && enrichedFile.tags.length > 0"
        display-as-text
        :maxTagsToShow="0"
        :modelValue="enrichedFile.tags"
        :readonly="true"
        state="view"
        :tagsClass="'cursor-pointer'"
      />

      <div
        v-if="shouldShowMetadata && Object.keys(fileMetadata).length > 0"
        class="flex flex-wrap gap-2 mb-1"
        :class="{ 'mt-1': enrichedFile.tags?.length }"
      >
        <CMetaDataBadge v-for="(meta, key) in fileMetadata" :key="key" :label="meta.label" :value="meta.value" />
      </div>

      <div class="flex flex-wrap gap-2">
        <CInfoBadge
          v-if="isExpired && isAdmin"
          v-tooltip="
            t('canvasUI.files.expiredTooltip', {
              time: dayjsInstance(expiresAt).format('HH:mm'),
              date: dayjsInstance(expiresAt).format('LL'),
            })
          "
          :background-color="themeVars.error2"
          :color="themeVars.error"
          fa-type="fa-kit"
          icon="regular-clock-circle-exclamation"
          :text="t('canvasUI.files.expired')"
        />
        <CInfoBadge
          v-if="isExpiring && !isExpired && isAdmin"
          v-tooltip="
            t('canvasUI.files.expiringTooltip', {
              time: dayjsInstance(expiresAt).format('HH:mm'),
              date: dayjsInstance(expiresAt).format('LL'),
            })
          "
          :background-color="themeVars.warning2"
          :color="themeVars.warning3"
          fa-type="fa-kit"
          icon="regular-clock-circle-exclamation"
          :text="t('canvasUI.files.expiring')"
        />
        <CInfoBadge
          v-if="enrichedFile.permissions?.can_share === false"
          :background-color="themeVars.error2"
          :color="themeVars.error"
          fa-type="fa-kit"
          icon="solid-share-from-square-slash"
          :text="t('canvasUI.files.notShareable')"
        />
        <CPopularityAndEngagementExtended
          v-if="Object.keys(popularityDict)?.length"
          class="gap-2 mr-2"
          :engagement="launchDarkly.show_popularity_icons ? popularityDict[enrichedFile.id]?.engagement : undefined"
          :is-recommended="!!recommendedSectionIds.includes(enrichedFile.id?.toString())"
          :popularity="launchDarkly.show_popularity_icons ? popularityDict[enrichedFile.id]?.popularity : undefined"
          :views="launchDarkly.show_popularity_icons ? (popularityDict[enrichedFile.id]?.views ?? 0) : 0"
        />
      </div>
    </div>

    <div class="flex justify-end h-full items-center gap-1 cursor-auto py-3 px-4" @click.stop>
      <div v-if="enrichedFile.is_deleted" class="ml-auto">
        <CButton size="small" text type="warning" @click.stop="emit('deletedFileClicked')">
          {{ t('canvasUI.canvasBuilder.deletedFile.deleteMessage') }}
        </CButton>
      </div>
      <div v-else-if="mode === CanvasBuilderMode.SHARING && isDefaultDisplayMode && canDownload">
        <DownloadButton
          v-tooltip="
            item.type === 'slide'
              ? t('canvasUI.components.ContentGrid.downloadSlide')
              : t('canvasUI.components.ContentGrid.downloadFile')
          "
          @click="downloadFile()"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CDynamicIndicator from '@lib/components/CDynamicIndicator/CDynamicIndicator.vue'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import CMetaDataBadge from '@lib/components/CMetaDataBadge/CMetaDataBadge.vue'
import CPopularityAndEngagementExtended from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementExtended.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'
import DownloadButton from '@canvas-builder/components/container/DownloadButton/DownloadButton.vue'

import useFileDisplayHelpers from '@lib/composables/useFileDisplayHelpers'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useImageAccessibility from '@lib/composables/useImageAccessibility'

import dayjsInstance from 'dayjs'
import tooltipDirective from '@lib/directives/tooltip'

import type { ContentGridItemFileProps, ContentGridItemSlideProps } from '@canvas-builder/types/canvas'
import { CanvasBuilderMode } from '@canvas-builder/types/canvas'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { RenderableFile } from '@lib/types/files'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import { FileContentTypeEnum, type CanvasRetrieve } from '@openapi'
import { getDefaultThumbnailUrl } from '@lib/utils/defaultThumbnail.util'

const { t } = useI18n()
const vTooltip = tooltipDirective

const props = defineProps<{
  item: ContentGridItemSlideProps | ContentGridItemFileProps
  usedInSectionId?: CanvasRetrieve['id']
  isLast?: boolean
}>()

const emit = defineEmits<{
  (e: 'deletedFileClicked'): void
}>()

const { renderContentType, isFileExpired, isFileExpiring } = useFileDisplayHelpers()
const { popularityDict } = useSectionPopularity(computed(() => ({}) as Record<string, any>))
const { recommendedSectionIds } = useRecommendations()
const themeVars = useThemeVars()
const { fileDataCache, isAdmin, isDefaultDisplayMode, mode, onDownloadFile, canvasMetadata } = useCanvas()

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const enrichedFile = computed(() => {
  const cachedFileData = fileDataCache.value.get(props.item.file.id)
  if (cachedFileData) {
    return {
      ...props.item.file,
      ...cachedFileData,
    }
  }
  return props.item.file
})

const thumbnailItem = computed(() => {
  const baseItem = enrichedFile.value

  if (!baseItem) {
    return undefined
  }

  if (props.item.type === 'slide' && props.item.slide?.url) {
    return {
      ...baseItem,
      thumbnail_url: props.item.slide.url,
    }
  }

  return baseItem
})

const defaultIcon = computed(() => {
  return props.item.type === 'slide' ? 'memo' : 'file'
})

const thumbnailUrl = computed(() => {
  if (props.item.type === 'slide' && props.item.slide?.url) {
    return props.item.slide.url
  }
  return enrichedFile.value?.thumbnail_url || null
})

const defaultThumbnailUrl = computed(() => {
  const file = enrichedFile.value
  if (!file) return null
  return getDefaultThumbnailUrl(file as any)
})

// Download functionality
const canDownload = computed(() => {
  if (enrichedFile.value?.permissions?.can_download === false) {
    return false
  }
  const canvasAllowDownload = canvasMetadata.value?.allow_download
  if (canvasAllowDownload === false) {
    return false
  }
  return true
})

const downloadFile = () => {
  enrichedFile.value && onDownloadFile.value?.(enrichedFile.value)
}

const shouldShowMetadata = computed(() => {
  return (
    pitcherSettings.value.enable_metadata_info_in_list_item &&
    launchDarkly.value.enable_extra_file_data &&
    !!(enrichedFile.value as any).metadata &&
    typeof (enrichedFile.value as any).metadata === 'object'
  )
})

const fileMetadata = computed(() => {
  if (!shouldShowMetadata.value) {
    return {}
  }

  const metadata = (enrichedFile.value as any).metadata

  // Pitcher setting for metadata keys to skip, with defaults
  const skipKeys = pitcherSettings.value?.skip_metadata_keys_in_list_item || [
    '_metadata_fields',
    '_sharepoint',
    '_sp_id',
    '_sp_ref',
    '_sync_',
    'file_edit_url',
    'sharepoint_base_url',
    'sharepoint_file_ref',
    'sharepoint_file_version',
    'sharepoint_hash',
    'sharepoint_ref',
    'sharepoint_url',
    'sharepoint_version',
    'sp_',
  ]

  if (!metadata || typeof metadata !== 'object') {
    return {}
  }

  const shouldSkipMetadataKey = (key: string) => {
    return skipKeys.some((pattern) => {
      return key.includes(pattern)
    })
  }

  const isSharePointFile =
    metadata &&
    (Object.keys(metadata).some(shouldSkipMetadataKey) ||
      Object.values(metadata).some((value) => typeof value === 'string' && value.includes('sharepoint.com')))

  const filteredMetadata = Object.fromEntries(
    Object.entries(metadata).filter(([key, value]) => {
      if (skipKeys.includes(key)) return false

      if (value === '' || (Array.isArray(value) && value.length === 0)) return false

      if (isSharePointFile && shouldSkipMetadataKey(key)) {
        return false
      }

      return true
    }),
  )

  const processedMetadata: Record<string, { label: string; value: string }> = {}
  Object.entries(filteredMetadata).forEach(([key, value]) => {
    processedMetadata[key] = {
      label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      value: Array.isArray(value) ? value.join(', ') : String(value),
    }
  })

  return processedMetadata
})

const expiresAt = computed(() => enrichedFile.value.expires_at || '')
const isExpired = computed(() => isFileExpired(expiresAt.value))
const isExpiring = computed(() => isFileExpiring(expiresAt.value))

// Image accessibility logic
const { hasValidThumbnail, handleImageError } = useImageAccessibility({
  thumbnailUrl,
  hasAIEnabled: computed(() => !!launchDarkly.value.enable_ai_generated_thumbnails),
  hasFile: computed(() => !!enrichedFile.value),
})
</script>

<style scoped lang="scss">
.content-grid-list-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.content-grid-list-item:hover {
  background-color: v-bind('themeVars.primary6');
}
</style>
